import { useMemo } from "react";
import { Card } from "../ui/card";
import { MapPin, User, Briefcase, Clock, Edit, DollarSign, Home } from "lucide-react";
import { format } from "date-fns";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { Separator } from "../ui/separator";
const formatPrice = (num) => parseFloat(Number(num).toFixed(3)).toString();
const BookingSummary = ({ formData, currentStep, onEditStep, agents = [] }) => {
  const agent = useMemo(() => {
    if (!formData.agentId) return null;
    return agents.find((a) => String(a.id) === String(formData.agentId)) || null;
  }, [formData.agentId, agents]);

  const secondAgent = useMemo(() => {
    if (!formData.secondAgentId) return null;
    return agents.find((a) => String(a.id) === String(formData.secondAgentId)) || null;
  }, [formData.secondAgentId, agents]);
  const formattedAddress = (() => {
    const { address, city, state, zipCode } = formData;
    if (!address && !city && !state && !zipCode) return "-----";
    const cityStateZip = [city, state, zipCode].filter(Boolean).join(" ");
    return [address, cityStateZip].filter(Boolean).join(", ");
  })();
  const formattedClient =
    formData.firstName && formData.lastName
      ? `${formData.firstName} ${formData.lastName}`
      : "-----";
  const formattedServices = formData.services.length > 0
    ? formData.services.map((id) => formData.serviceNames?.[id] || id).join(", ")
    : "-----";
  const formattedDateTime =
    formData.appointmentDate && formData.appointmentTime
      ? `${format(formData.appointmentDate, "MMMM d, yyyy")} at ${formData.appointmentTime}`
      : "-----";
  const totalPrice = Object.values(formData.servicePrices || {}).reduce(
    (sum, price) => sum + price,
    0
  );
  const totalDurationMinutes = Object.values(formData.serviceDurations || {}).reduce(
    (sum, duration) => sum + Number(duration || 0),
    0
  );
  const formatTotalDuration = (minutes) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) return `${h} ${h === 1 ? "hour" : "hours"}`;
    return `${h}h ${m}m`;
  };
  const getInspectorName = () => {
    if (!formData.preferredInspector || formData.preferredInspector === "any") {
      return "Any available inspector";
    }
    const match = (formData.availableInspectors || []).find(
      (i) => String(i.id) === String(formData.preferredInspector)
    );
    return match?.name || "Any available inspector";
  };
  const getPropertyDetails = () => {
    const details = [];
    if (formData.squareFeet) details.push(`${formData.squareFeet} sq ft`);
    if (formData.yearBuilt) details.push(`Built ${formData.yearBuilt}`);
    if (formData.bedrooms) details.push(`${formData.bedrooms} bed`);
    if (formData.bathrooms) details.push(`${formData.bathrooms} bath`);
    return details.join(" \u2022 ");
  };
  const getPropertyExtras = () => {
    const extras = [];
    if (formData.hasGarage !== null) extras.push(formData.hasGarage ? "Has garage" : "No garage");
    return extras.join(" \u2022 ");
  };
  return (
    <div className="h-full flex flex-col border-gray-200 bg-gray-50 p-4">
      <h2 className="text-lg font-bold !mb-2">Booking Summary</h2>
      <div className="space-y-3">
        <Card className="p-4 relative">
          <div className="flex items-start">
            <User className="h-5 w-5 mr-2 text-gray-500 mt-1" />
            <div>
              <h3 className="font-medium text-gray-900 text-base">Client Info</h3>
              <p className="text-gray-700 mt-1 text-sm">{formattedClient}</p>
              {(formData.email || formData.phone) && (
                <div className="text-gray-500 mt-1 text-xs">
                  {formData.email && <p className="m-0">{formData.email}</p>}
                  {formData.phone && <p className="m-0">{formData.phone}</p>}
                </div>
              )}
              {formData.secondaryFirstName && formData.secondaryLastName && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-gray-500 text-xs">Secondary client</p>
                  <p className="text-gray-700 text-sm">
                    {formData.secondaryFirstName} {formData.secondaryLastName}
                  </p>
                  {(formData.secondaryEmail || formData.secondaryPhone) && (
                    <div className="text-gray-500 text-xs mt-0.5">
                      {formData.secondaryEmail && <p className="m-0">{formData.secondaryEmail}</p>}
                      {formData.secondaryPhone && <p className="m-0">{formData.secondaryPhone}</p>}
                    </div>
                  )}
                </div>
              )}
              {agent && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-gray-500 text-xs">
                    {formData.agentType === "buyers"
                      ? "Buyer's Agent"
                      : formData.agentType === "sellers"
                        ? "Seller's Agent"
                        : "Agent"}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {agent.first_name} {agent.last_name}
                  </p>
                  <p className="text-gray-500 text-xs">{agent.agentdata?.agency || agent.agency}</p>
                  <div className="text-gray-500 text-xs">
                    {agent.email && <p className="m-0">{agent.email}</p>}
                    {agent.phone && <p className="m-0">{agent.phone}</p>}
                  </div>
                </div>
              )}
              {secondAgent && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-gray-500 text-xs">
                    {formData.secondAgentType === "buyers"
                      ? "Buyer's Agent"
                      : formData.secondAgentType === "sellers"
                        ? "Seller's Agent"
                        : "Agent"}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {secondAgent.first_name} {secondAgent.last_name}
                  </p>
                  <p className="text-gray-500 text-xs">{secondAgent.agentdata?.agency || secondAgent.agency}</p>
                  <div className="text-gray-500 text-xs">
                    {secondAgent.email && <p className="m-0">{secondAgent.email}</p>}
                    {secondAgent.phone && <p className="m-0">{secondAgent.phone}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
          {(formData.firstName || formData.email) && currentStep !== 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => onEditStep(0)}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit Client</span>
            </Button>
          )}
        </Card>
        <Card className={cn("p-4 relative", currentStep < 1 && "opacity-70")}>
          <div className="flex items-start">
            <MapPin className="h-5 w-5 mr-2 text-gray-500 mt-1" />
            <div>
              <h3 className="font-medium text-gray-900 text-base">Address</h3>
              <p className="text-gray-700 mt-1 text-sm">{formattedAddress}</p>
            </div>
          </div>
          {formData.address && currentStep !== 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => onEditStep(1)}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit Address</span>
            </Button>
          )}
        </Card>
        <Card className={cn("p-4 relative", currentStep < 2 && "opacity-70")}>
          <div className="flex items-start">
            <Home className="h-5 w-5 mr-2 text-gray-500 mt-1" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 text-base">Property Info</h3>
              { formData.squareFeet || formData.yearBuilt || formData.bedrooms || formData.bathrooms || formData.hasGarage !== null || formData.gateCode || formData.propertyNotes ? (
                <>
          
                  {getPropertyDetails() && (
                    <p className="text-gray-500 mt-1 text-xs">{getPropertyDetails()}</p>
                  )}
                  {getPropertyExtras() && (
                    <p className="text-gray-500 mt-1 text-xs">{getPropertyExtras()}</p>
                  )}
                  {formData.gateCode && (
                    <p className="text-gray-500 mt-1 text-xs">Gate Code: {formData.gateCode}</p>
                  )}
                  {formData.propertyNotes && (
                    <p className="text-gray-500 mt-1 text-xs line-clamp-2 break-all">
                      Notes: {formData.propertyNotes}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-gray-700 mt-1 text-sm">-----</p>
              )}
            </div>
          </div>
          {currentStep !== 2 &&
            (formData.squareFeet ||
              formData.yearBuilt ||
              formData.bedrooms ||
              formData.bathrooms ||
              formData.hasGarage !== null ||
              formData.gateCode ||
              formData.propertyNotes) && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={() => onEditStep(2)}
              >
                <Edit className="h-4 w-4" />
                <span className="sr-only">Edit Property Info</span>
              </Button>
            )}
        </Card>
        <Card className={cn("p-4 relative", currentStep < 3 && "opacity-70")}>
          <div className="flex items-start">
            <Briefcase className="h-5 w-5 mr-2 text-gray-500 mt-1" />
            <div className="w-full">
              <h3 className="font-medium text-gray-900 text-base">Services</h3>
              {formData.services.length > 0 ? (
                <div className="mt-2 space-y-2 w-full">
                  {formData.services.map((id) => (
                    <div className="flex justify-between items-center text-sm" key={id}>
                      <span className="text-sm">{formData.serviceNames?.[id] || id}</span>
                      <span className="font-medium text-sm">
                        ${formatPrice(formData.servicePrices?.[id] || 0)}
                      </span>
                    </div>
                  ))}
                  {formData.services.length > 0 && (
                    <>
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Duration:</span>
                        <span className="text-sm">{formatTotalDuration(totalDurationMinutes)}</span>
                      </div>
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-sm">Total:</span>
                        <span className="text-sm">${formatPrice(totalPrice)}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-700 mt-1 text-sm">-----</p>
              )}
              {formData.additionalNotes && (
                <p className="text-gray-500 mt-3 text-xs line-clamp-2 break-all">Note: {formData.additionalNotes}</p>
              )}
            </div>
          </div>
          {formData.services.length > 0 && currentStep !== 3 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => onEditStep(3)}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit Services</span>
            </Button>
          )}
        </Card>
        <Card className={cn("p-4 relative", currentStep < 4 && "opacity-70")}>
          <div className="flex items-start">
            <Clock className="h-5 w-5 mr-2 text-gray-500 mt-1" />
            <div>
              <h3 className="font-medium text-gray-900 text-base">Date/Time</h3>
              <p className="text-gray-700 mt-1 text-sm">{formattedDateTime}</p>
              {formData.preferredInspector && formData.appointmentDate && formData.appointmentTime && (
                <p className="text-gray-500 mt-1 text-xs">Inspector: {getInspectorName()}</p>
              )}
            </div>
          </div>
          {formData.appointmentDate && currentStep !== 4 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => onEditStep(4)}
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit Date/Time</span>
            </Button>
          )}
        </Card>
      </div>
      {formData.services.length > 0 && (
        <Card className="p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-gray-700" />
              <span className="font-semibold text-lg">Order Total:</span>
            </div>
            <span className="font-bold text-lg">${formatPrice(totalPrice)}</span>
          </div>
        </Card>
      )}
    </div>
  );
};
export default BookingSummary;
